import * as React from 'react'
import { create } from '@bufbuild/protobuf'
import { ConnectError, Code } from '@connectrpc/connect'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { VoidInvoiceRequestSchema } from '@/gen/cortado/v1/invoices_pb'
import { getErrorDescription } from '@/lib/error-utils'
import { captureError } from '@/lib/sentry'
import { useVoidInvoice } from '@/stores/payments.store'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface VoidInvoiceDialogProps {
  invoiceId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Confirms and voids an invoice. A FailedPrecondition from the first attempt
 * means the invoice has payments; the dialog then shows the server's warning
 * and the second confirm retries with force.
 */
export function VoidInvoiceDialog({ invoiceId, open, onOpenChange }: VoidInvoiceDialogProps) {
  const [voidReason, setVoidReason] = React.useState('')
  const [paymentWarning, setPaymentWarning] = React.useState<string | null>(null)
  const { voidInvoice, isVoiding } = useVoidInvoice(invoiceId)

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setPaymentWarning(null)
      setVoidReason('')
    }
  }

  const handleVoidInvoice = async (force: boolean) => {
    try {
      await voidInvoice(
        create(VoidInvoiceRequestSchema, {
          invoiceId,
          reason: voidReason || undefined,
          force,
        })
      )
      handleOpenChange(false)
      toast.success('Invoice voided successfully')
    } catch (error) {
      if (!force && error instanceof ConnectError && error.code === Code.FailedPrecondition) {
        setPaymentWarning(error.message)
      } else {
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'invoice-void' })
        toast.error('Failed to void invoice', {
          description: getErrorDescription(error),
        })
      }
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Void Invoice
            </span>
          </AlertDialogTitle>
          <AlertDialogDescription>
            {paymentWarning
              ? 'This invoice has existing payments. Voiding will NOT automatically refund them.'
              : 'This action cannot be undone. Voiding this invoice will cancel all associated payment schedules and mark the invoice as cancelled.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {paymentWarning && (
          <div className="rounded-md border border-status-warning-border bg-status-warning-bg p-3 text-sm text-status-warning-fg">
            <p className="font-medium">Warning: {paymentWarning}</p>
            <p className="mt-1 text-xs text-status-warning-fg">
              You will need to process refunds separately if needed.
            </p>
          </div>
        )}
        {!paymentWarning && (
          <div className="space-y-2 py-2">
            <Label htmlFor="void-reason">Reason (optional)</Label>
            <Textarea
              id="void-reason"
              placeholder="Enter reason for voiding..."
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isVoiding}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              handleVoidInvoice(!!paymentWarning)
            }}
            disabled={isVoiding}
            className="bg-destructive text-white hover:bg-destructive/90"
          >
            {isVoiding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Voiding...
              </>
            ) : paymentWarning ? (
              'Void Anyway'
            ) : (
              'Void Invoice'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
